/**
 * React Query hooks for data fetching with caching
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

// Query keys for cache management
export const queryKeys = {
  products: ['products'] as const,
  product: (slug: string) => ['product', slug] as const,
  categories: ['categories'] as const,
  checkoutConfig: ['checkout', 'config'] as const,
  adminProducts: ['admin', 'products'] as const,
  adminProduct: (id: string) => ['admin', 'product', id] as const,
  adminOrders: (filters?: Record<string, any>) => ['admin', 'orders', filters] as const,
  adminOrder: (id: string) => ['admin', 'order', id] as const,
};

// ============ PUBLIC HOOKS ============

/**
 * Fetch all products (cached for 5 minutes)
 */
export function useProducts() {
  return useQuery({
    queryKey: queryKeys.products,
    queryFn: () => api.products.getAll(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });
}

/**
 * Fetch single product by slug (cached for 5 minutes)
 */
export function useProduct(slug: string) {
  return useQuery({
    queryKey: queryKeys.product(slug),
    queryFn: () => api.products.getBySlug(slug),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!slug, // Don't fetch if no slug
  });
}

/**
 * Fetch all categories (cached for 10 minutes)
 */
export function useCategories() {
  return useQuery({
    queryKey: queryKeys.categories,
    queryFn: () => api.categories.getAll(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Fetch checkout/shipping config (cached for 10 minutes)
 */
export function useCheckoutConfig() {
  return useQuery({
    queryKey: queryKeys.checkoutConfig,
    queryFn: () => api.checkout.getConfig(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Calculate pricing (not cached - always fresh)
 */
export function useCalculatePricing() {
  return useMutation({
    mutationFn: (data: {
      items: Array<{ productId: string; price: number; quantity: number }>;
      shippingMethod?: 'inpost' | 'courier';
    }) => api.checkout.calculatePricing(data),
  });
}

/**
 * Create checkout session
 */
export function useCreateCheckoutSession() {
  return useMutation({
    mutationFn: (data: Parameters<typeof api.checkout.createSession>[0]) =>
      api.checkout.createSession(data),
  });
}

/**
 * Send contact form
 */
export function useSendContact() {
  return useMutation({
    mutationFn: (data: { name: string; email: string; subject?: string; message: string }) =>
      api.contact.send(data),
  });
}

// ============ ADMIN HOOKS ============

/**
 * Fetch all products for admin (cached for 2 minutes)
 */
export function useAdminProducts() {
  return useQuery({
    queryKey: queryKeys.adminProducts,
    queryFn: async () => {
      const response = await api.get<{ products: any[] }>('/api/admin/products');
      return response.products;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Fetch single product for admin edit
 */
export function useAdminProduct(id: string) {
  return useQuery({
    queryKey: queryKeys.adminProduct(id),
    queryFn: async () => {
      const response = await api.get<{ product: any }>(`/api/admin/products/${id}`);
      return response.product;
    },
    enabled: !!id && id !== 'new',
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Fetch orders for admin (cached for 1 minute)
 */
export function useAdminOrders(filters?: { status?: string; limit?: number }) {
  return useQuery({
    queryKey: queryKeys.adminOrders(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status) params.set('status', filters.status);
      if (filters?.limit) params.set('limit', String(filters.limit));
      const query = params.toString();
      const response = await api.get<{ orders: any[] }>(`/api/admin/orders${query ? `?${query}` : ''}`);
      return response.orders;
    },
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Fetch single order for admin
 */
export function useAdminOrder(id: string) {
  return useQuery({
    queryKey: queryKeys.adminOrder(id),
    queryFn: async () => {
      const response = await api.get<{ order: any }>(`/api/admin/orders/${id}`);
      return response.order;
    },
    enabled: !!id,
    staleTime: 30 * 1000, // 30 seconds
  });
}

// ============ MUTATION HOOKS ============

/**
 * Invalidate queries after mutations
 */
export function useInvalidateProducts() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['products'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
  };
}

export function useInvalidateOrders() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
  };
}

